import React, { useCallback, useEffect, useMemo, useState } from 'react'
import moment from 'moment-timezone'
import { Spin } from 'antd'
import { searchListings, searchTimeslots } from 'containers/HeaderContainer/HeaderContainer.duck'
import { useDispatch, useSelector } from 'react-redux'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { formatDateStringToTz, getDefaultTimeZoneOnBrowser } from 'util/dates'
import config from '../../config'
import { useLocation } from 'react-router-dom'
import FieldDateInput from 'components/FieldDateInput/FieldDateInput'
import { Field, Form } from 'react-final-form'
import { format } from 'date-fns'
import FacilitiesInlineListingCard from './FacilitiesInlineListingCard'
import css from './FacilitiesComponent.css'
import classNames from 'classnames'

dayjs.extend(utc)
dayjs.extend(timezone)

const getNearestTime = () => {
  const now = new Date()
  const minutes = now.getMinutes()
  const remainder = minutes % 15
  const addMinutes = remainder === 0 ? 0 : 15 - remainder

  now.setMinutes(minutes + addMinutes)
  now.setSeconds(0)
  now.setMilliseconds(0)

  return moment(now).format('h:mm A')
}

const todayFormatted = format(new Date(), 'EEE, MMM d')

const FacilitiesComponent = ({ className, listings, allClassListingReviews, showAll }) => {
  const dispatch = useDispatch()
  const searchedTimeslots = useSelector(state => state.HeaderSection.searchedTimeslots)
  const searchInProgress = useSelector(state => state.HeaderSection.searchInProgress)
  const location = useLocation()

  const [searchValues, setSearchValues] = useState({
    sport: 'padel tennis',
    date: dayjs(),
    time: getNearestTime(),
  })
  const [fetchedTimeslotIds, setFetchedTimeslotIds] = useState(new Set())

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const category = params.get('category')

    if (category) {
      setSearchValues(prev => ({ ...prev, sport: category }))
    }
  }, [location])

  const defaultTimeZone = useCallback(() => {
    return typeof window !== 'undefined'
      ? getDefaultTimeZoneOnBrowser()
      : config.custom.dateRangeLengthFilterConfig.searchTimeZone || 'Europe/London'
  }, [])

  const { startDate, endDate } = useMemo(() => {
    const { date, time } = searchValues

    if (!date || !time) {
      console.warn('Missing date or time in searchValues:', searchValues)
      return { startDate: null, endDate: null }
    }

    const formattedTime = moment(time, 'hh:mm A').format('HH:mm')

    const formattedDateString = moment(date).format('YYYY-MM-DD')

    const combinedDateTimeString = `${formattedDateString} ${formattedTime}`

    const selectedDateTime = moment(combinedDateTimeString, 'YYYY-MM-DD HH:mm')

    if (!selectedDateTime.isValid()) {
      console.error('Invalid combined datetime in useMemo:', selectedDateTime)
      return { startDate: null, endDate: null }
    }

    const timeZone = defaultTimeZone()

    const isToday = dayjs(date)
      .tz(timeZone)
      .isSame(dayjs().tz(timeZone), 'day')

    const start = isToday
      ? moment.tz(timeZone).toISOString()
      : selectedDateTime
          .clone()
          .startOf('day')
          .toISOString()

    const end = isToday
      ? moment
          .tz(timeZone)
          .endOf('day')
          .toISOString()
      : moment
          .tz(date, timeZone)
          .endOf('day')
          .toISOString()

    return {
      startDate: formatDateStringToTz(start, timeZone),
      endDate: formatDateStringToTz(end, timeZone),
    }
  }, [searchValues, defaultTimeZone])

  const handleChange = (event, field) => {
    if (field === 'date') {
      const formattedDate = dayjs(event.date).format('YYYY-MM-DD')
      setSearchValues(prev => ({ ...prev, date: formattedDate }))
    } else if (field === 'time') {
      setSearchValues(prev => ({ ...prev, time: event }))
    }
  }

  const onSubmitFun = useCallback(
    formValues => {
      const { date, time } = formValues
      setFetchedTimeslotIds(new Set())

      const formattedDateString = date?.format('YYYY-MM-DD')
      if (!formattedDateString) {
        console.error('Invalid date object:', date)
        return
      }
      const formattedTime = moment(time, 'hh:mm A').format('HH:mm')
      const combinedDateTimeString = `${formattedDateString} ${formattedTime}`
      const selectedDateTime = moment(combinedDateTimeString, 'YYYY-MM-DD HH:mm')

      if (!selectedDateTime.isValid()) {
        console.error('Invalid combined datetime:', selectedDateTime)
        return
      }

      const searchParams = {
        pub_sub_category: searchValues.sport,
        minDuration: 30,
        address: 'Singapore',
        bounds: '1.4110994,103.9577613,1.2642103,103.6055395',
        pub_listing_type: 'facility',
      }

      dispatch(searchListings(searchParams))
    },
    [dispatch, searchValues, defaultTimeZone]
  )

  useEffect(() => {
    if (searchValues.date && searchValues.time && searchValues.sport) {
      onSubmitFun({
        date: dayjs.isDayjs(searchValues.date) ? searchValues.date : dayjs(searchValues.date),
        time: searchValues.time || getNearestTime(),
      })
    }
  }, [searchValues, searchValues.date, searchValues.time, searchValues.sport, onSubmitFun])

  useEffect(() => {
    if (listings && listings.length > 0 && startDate && endDate) {
      const newIds = listings
        .map(listing => listing.id.uuid)
        .filter(id => !fetchedTimeslotIds.has(id))

      if (newIds.length > 0) {
        newIds.forEach(listingId => {
          dispatch(searchTimeslots({ listingId: [listingId], start: startDate, end: endDate }))
        })
        setFetchedTimeslotIds(prev => new Set([...prev, ...newIds]))
      }
    }
  }, [listings, searchValues.sport, dispatch, fetchedTimeslotIds])

  const handleReset = () => {
    const nearestTime = getNearestTime()
    const today = dayjs()

    setSearchValues({
      sport: searchValues.sport,
      date: today,
      time: nearestTime,
    })

    const formattedDateString = today?.format('YYYY-MM-DD')
    if (!formattedDateString) {
      console.error('Invalid date object:', today)
      return
    }
    const formattedTime = moment(nearestTime, 'hh:mm A').format('HH:mm')
    const combinedDateTimeString = `${formattedDateString} ${formattedTime}`
    const selectedDateTime = moment(combinedDateTimeString, 'YYYY-MM-DD HH:mm')

    if (!selectedDateTime.isValid()) {
      console.error('Invalid combined datetime:', selectedDateTime)
      return
    }

    const searchParams = {
      pub_sub_category: searchValues.sport,
      minDuration: 30,
      address: 'Singapore',
      bounds: '1.4110994,103.9577613,1.2642103,103.6055395',
      pub_listing_type: 'facility',
    }

    dispatch(searchListings(searchParams))
  }

  return (
    <div>
      <Form
        onSubmit={onSubmitFun}
        initialValues={{
          date: dayjs.isDayjs(searchValues.date) ? searchValues.date : dayjs(searchValues.date),
          time: searchValues.time || getNearestTime(),
        }}
        subscription={{}}
        render={({ handleSubmit, values }) => (
          <form className={css.outerDateSelection} onSubmit={handleSubmit}>
            <Field
              name='date'
              render={({ input, meta }) => (
                <div className={classNames(css.field, css.startDate)}>
                  <FieldDateInput
                    className={css.fieldDateInput}
                    labelClassName={css.bookingLabel}
                    inputClassName={css.bookingInputWrapper}
                    fullBorderColor={true}
                    name='date'
                    id='bookingStartDate'
                    label={'PICK A DATE'}
                    placeholderText={
                      searchValues.date
                        ? dayjs(searchValues.date).format('ddd, MMM D')
                        : todayFormatted
                    }
                    useMobileMargins
                    onChange={date => {
                      input.onChange(date)
                      handleChange(date, 'date')
                    }}
                    value={input.value}
                  />
                </div>
              )}
            />

            {/* <Field
              name='time'
              render={({ input, meta }) => (
                <div className={css.field}>
                  <FieldSelectOutline
                    {...input}
                    id='bookingStartTime'
                    className={css.fieldSelect}
                    labelClassName={css.bookingLabel}
                    selectClassName={css.select}
                    onChange={e => handleChange(e, 'time')}
                    label={'START TIME'}
                    svgIcon={
                      <svg
                        width='20'
                        height='20'
                        viewBox='0 0 20 20'
                        fill='none'
                        xmlns='http://www.w3.org/2000/svg'
                      >
                        <path
                          d='M10 5V10H15'
                          stroke='#565151'
                          strokeOpacity='0.5'
                          strokeWidth='1.5'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        />
                        <path
                          d='M13.4492 2.41168C12.3982 1.93326 11.2304 1.66675 10.0003 1.66675C5.39795 1.66675 1.66699 5.39771 1.66699 10.0001C1.66699 14.6024 5.39795 18.3334 10.0003 18.3334C14.6027 18.3334 18.3337 14.6024 18.3337 10.0001C18.3337 7.9909 17.6226 6.14778 16.4384 4.70865'
                          stroke='#565151'
                          strokeOpacity='0.5'
                          strokeWidth='1.5'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        />
                      </svg>
                    }
                  >
                    {timeOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </FieldSelectOutline>
                </div>
              )}
            /> */}
            <div className={css.searchIconWrap}>
              <button type='submit' className={css.searchIcon}>
                <svg fill='#000000' height='14px' width='14px' viewBox='0 0 488.4 488.4'>
                  <path d='M0,203.25c0,112.1,91.2,203.2,203.2,203.2c51.6,0,98.8-19.4,134.7-51.2l129.5,129.5c2.4,2.4,5.5,3.6,8.7,3.6 s6.3-1.2,8.7-3.6c4.8-4.8,4.8-12.5,0-17.3l-129.6-129.5c31.8-35.9,51.2-83,51.2-134.7c0-112.1-91.2-203.2-203.2-203.2 S0,91.15,0,203.25z M381.9,203.25c0,98.5-80.2,178.7-178.7,178.7s-178.7-80.2-178.7-178.7s80.2-178.7,178.7-178.7 S381.9,104.65,381.9,203.25z' />
                </svg>
                Search
              </button>
              <button onClick={handleReset} className={css.searchIcon}>
                <svg height='16px' width='16px' viewBox='0 0 20 20' className={css.icon}>
                  <g
                    fill='none'
                    stroke='currentColor'
                    stroke-linecap='round'
                    stroke-linejoin='round'
                    transform='matrix(0 1 1 0 2.5 2.5)'
                  >
                    <path d='m3.98652376 1.07807068c-2.38377179 1.38514556-3.98652376 3.96636605-3.98652376 6.92192932 0 4.418278 3.581722 8 8 8s8-3.581722 8-8-3.581722-8-8-8' />
                    <path d='m4 1v4h-4' transform='matrix(1 0 0 -1 0 6)' />
                  </g>
                </svg>
                Reset
              </button>
            </div>
          </form>
        )}
      />

      {searchInProgress ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
          <Spin size='large' tip='Searching facilities...' />
        </div>
      ) : listings.length > 0 ? (
        listings
          .sort((a, b) => {
            return (b.attributes.metadata.promoted || 0) - (a.attributes.metadata.promoted || 0)
          })
          .map(l => {
            const timeslotsForListing = searchedTimeslots[l.id.uuid] || []
            return (
              <FacilitiesInlineListingCard
                className={className}
                key={l.id.uuid}
                listing={l}
                review={allClassListingReviews ? allClassListingReviews[l.id.uuid] : []}
                selectedDate={searchValues.date}
                showAll={showAll}
                startDate={startDate}
                endDate={endDate}
                searchValues={searchValues}
                timeslots={timeslotsForListing}
                noTimeslotsMessage='No time slots available for this facility at the selected time.'
              />
            )
          })
      ) : (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            padding: '40px',
            fontSize: '16px',
            color: '#777',
          }}
        >
          No facilities available for applied filters
        </div>
      )}
    </div>
  )
}

export default FacilitiesComponent
